import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap } from 'rxjs';
import { offeringActions } from './offering.actions';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { OfferingService } from './offering.service';
import { filter, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { offeringFeature } from './offering.state';
import { HelpersService } from '@letly/services';
import { offerErrorMessage } from './offer-error-message';

@Injectable()
export class OfferingEffects {

  private store = inject(Store);
  private actions$ = inject(Actions);
  private messageService = inject(MessageService);
  private offeringService = inject(OfferingService);
  private helperService = inject(HelpersService);

  loadTenantOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.loadTenantOffers),
      withLatestFrom(this.store.select(offeringFeature.selectTenantFilters)),
      switchMap(([action, filters]) =>
        this.offeringService.getOffers(this.helperService.cleanFilters(filters)).pipe(
          map(res => {
            if (res.success) {
              return offeringActions.onLoadTenantOffers({
                status: 'success',
                message: null,
                items: res.data.items,
                pageNumber: res.data.pageNumber,
                pageSize: res.data.pageSize,
                totalNumberOfItems: res.data.totalNumberOfItems,
                hasMore: res.data.hasMore,
                append: (action as any).append
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offeringActions.onLoadTenantOffers({
                status: 'error',
                message: res.niceErrorMessage,
                items: null,
                pageNumber: 0,
                pageSize: 0,
                totalNumberOfItems: 0,
                hasMore: false
              });
            }
          })
        )
      )
    )
  );

  loadLandlordOffersByProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.loadLandlordOffers),
      withLatestFrom(this.store.select(offeringFeature.selectLandlordFilters)),
      switchMap(([action, filters]) =>
        this.offeringService.getLandlordOffersByProperty(action.propertyId, this.helperService.cleanFilters(filters)).pipe(
          map(res => {
            if (res.success) {
              return offeringActions.onLoadLandlordOffers({
                status: 'success',
                message: null,
                items: res.data.items,
                pageNumber: res.data.pageNumber,
                pageSize: res.data.pageSize,
                totalNumberOfItems: res.data.totalNumberOfItems,
                hasMore: res.data.hasMore,
                append: action.append
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offeringActions.onLoadLandlordOffers({
                status: 'error',
                message: res.niceErrorMessage,
                items: null,
                pageNumber: 0,
                pageSize: 0,
                totalNumberOfItems: 0,
                hasMore: false
              });
            }
          })
        )
      )
    )
  );

  loadAllLandlordOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.loadAllLandlordOffers),
      withLatestFrom(this.store.select(offeringFeature.selectAllLandlordFilters)),
      switchMap(([action, filters]) =>
        this.offeringService.getAllLandlordOffers(this.helperService.cleanFilters(filters)).pipe(
          map(res => {
            if (res.success) {
              return offeringActions.onLoadAllLandlordOffers({
                status: 'success',
                message: null,
                items: res.data.items,
                pageNumber: res.data.pageNumber,
                pageSize: res.data.pageSize,
                totalNumberOfItems: res.data.totalNumberOfItems,
                hasMore: res.data.hasMore,
                append: action.append
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offeringActions.onLoadAllLandlordOffers({
                status: 'error',
                message: res.niceErrorMessage,
                items: null,
                pageNumber: 0,
                pageSize: 0,
                totalNumberOfItems: 0,
                hasMore: false
              });
            }
          })
        )
      )
    )
  );

  loadOfferDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.loadOfferDetail, offeringActions.refreshOfferDetail),
      mergeMap(action =>
        this.offeringService.getOfferById(action.id).pipe(
          map(res => {
            if (res.success) {
              return offeringActions.onLoadOfferDetail({
                status: 'success',
                message: null,
                id: action.id,
                offer: res.data
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offeringActions.onLoadOfferDetail({
                status: 'error',
                message: null,
                id: action.id,
                offer: res.data
              });
            }
          })
        )
      )
    )
  );

  loadOfferDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.loadOfferDocuments),
      mergeMap(action =>
        this.offeringService.getOfferDocuments(action.id).pipe(
          map(res => {
            if (res.success) {
              return offeringActions.onLoadOfferDocuments({
                status: 'success',
                message: null,
                id: action.id,
                documents: res.data?.items ?? []
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offeringActions.onLoadOfferDocuments({
                status: 'error',
                message: res.niceErrorMessage,
                id: action.id,
                documents: []
              });
            }
          })
        )
      )
    )
  );

  loadTenantProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.loadTenantProfile),
      mergeMap(action =>
        this.offeringService.getOfferTenantProfile(action.id, action.tenantId).pipe(
          map(res => {
            if (res.success) {
              return offeringActions.onLoadTenantProfile({
                status: 'success',
                message: null,
                id: action.id,
                tenantId: action.tenantId,
                tenantProfile: res.data
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offeringActions.onLoadTenantProfile({
                status: 'error',
                message: res.niceErrorMessage,
                id: action.id,
                tenantId: action.tenantId,
                tenantProfile: null
              });
            }
          })
        )
      )
    )
  );

  createOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.createOffer),
      switchMap(({ data }) =>
        this.offeringService.makeOffer(data).pipe(
          switchMap(res => {
              if (res.success) {
                return [
                  offeringActions.onCreateOffer({
                    status: 'success',
                    message: '',
                    offer: res.data
                  }),
                  offeringActions.loadTenantOffers({ append: false })
                ];
              } else {
                return [
                  offeringActions.onCreateOffer({
                    status: 'error',
                    message: offerErrorMessage(res),
                    offer: null
                  })
                ];
              }
            }
          )
        )
      ))
  );

  withdrawOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.withdrawOffer),
      switchMap(({ id, reason }) =>
        this.offeringService.withdrawOffer(id, reason)
          .pipe(
            switchMap(res => {
              if (res.success) {
                this.messageService.showSnackbar($localize`:@@offering.toast.withdraw.success:Offer withdrawn.`, ToastSeverity.Success);
                return [
                  offeringActions.onWithdrawOffer({ id, status: 'success', message: '' }),
                  offeringActions.loadTenantOffers({ append: false })
                ];
              }
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return [offeringActions.onWithdrawOffer({ id, status: 'error', message: res.niceErrorMessage })];
            })
          )
      )
    )
  );

  acceptOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.acceptOffer),
      switchMap(({ offerId }) =>
        this.offeringService.acceptOffer(offerId)
          .pipe(
            switchMap(res => {
              if (res.success) {
                this.messageService.showSnackbar($localize`:@@offering.toast.accept.success:Offer accepted.`, ToastSeverity.Success);
                return [
                  offeringActions.onAcceptOffer({ offerId, status: 'success', message: '', offer: res.data }),
                  offeringActions.loadTenantOffers({ append: false })
                ];
              }
              this.messageService.showSnackbar(offerErrorMessage(res), ToastSeverity.Error);
              return [offeringActions.onAcceptOffer({ offerId, status: 'error', message: offerErrorMessage(res) })];
            })
          )
      )
    )
  );

  counterOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.counterOffer),
      switchMap(({ offerId, terms }) =>
        this.offeringService.counterOffer(offerId, terms)
          .pipe(
            switchMap(res => {
              if (res.success) {
                this.messageService.showSnackbar($localize`:@@offering.toast.counter.success:Counter offer submitted.`, ToastSeverity.Success);
                return [
                  offeringActions.onCounterOffer({ offerId, status: 'success', message: '', offer: res.data }),
                  offeringActions.loadTenantOffers({ append: false })
                ];
              }
              this.messageService.showSnackbar(offerErrorMessage(res), ToastSeverity.Error);
              return [offeringActions.onCounterOffer({ offerId, status: 'error', message: offerErrorMessage(res) })];
            })
          )
      )
    )
  );

  rejectOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.rejectOffer),
      switchMap(({ offerId, rejectionReason }) =>
        this.offeringService.rejectOffer(offerId, rejectionReason)
          .pipe(
            switchMap(res => {
              if (res.success) {
                this.messageService.showSnackbar($localize`:@@offering.toast.reject.success:Offer declined.`, ToastSeverity.Success);
                return [
                  offeringActions.onRejectOffer({ offerId, status: 'success', message: '', offer: res.data }),
                  offeringActions.loadTenantOffers({ append: false })
                ];
              }
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return [offeringActions.onRejectOffer({ offerId, status: 'error', message: res.niceErrorMessage })];
            })
          )
      )
    )
  );


  offerLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offeringActions.loadOfferLogs),
      mergeMap((action) =>
        this.store.select(offeringFeature.selectOfferLogFilters(action.offerId)).pipe(
          take(1),
          switchMap(filters =>
            this.offeringService.getOfferLogs(action.offerId, this.helperService.cleanFilters(filters))
              .pipe(
                map(res => {
                  if (res.success) {
                    return offeringActions.onLoadOfferLogs({
                      offerId: action.offerId,
                      status: 'success',
                      message: '',
                      logs: res.data,
                      append: action.append
                    });
                  }
                  this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
                  return offeringActions.onLoadOfferLogs({
                    offerId: action.offerId,
                    status: 'error',
                    message: res.niceErrorMessage,
                    append: action.append
                  });
                })
              )
          )
        )
      )
    )
  );

  reloadOfferAfterAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        offeringActions.onWithdrawOffer,
        offeringActions.onAcceptOffer,
        offeringActions.onCounterOffer,
        offeringActions.onRejectOffer
      ),
      filter(action => action.status === 'success'),
      map(action => offeringActions.loadOfferDetail({ id: (action as any).id || (action as any).offerId }))
    )
  );

}
